<template>
  <div>
    <div class="d-flex align-items-end pt-2 pb-6">
      <div class="d-flex flex-column">
        <h1 class="h5 mb-2">
          Script Installation
        </h1>
        <b-breadcrumb class="breadcrumb-transparent breadcrumb-line p-0 m-0">
          <b-breadcrumb-item :to="{ name: 'Dashboard' }">
            <i class="fa fa-home fa-sm mr-2"></i>
            Home
          </b-breadcrumb-item>
          <b-breadcrumb-item :to="{ name: 'ServersList'}">Servers</b-breadcrumb-item>
          <b-breadcrumb-item active>Script Installation</b-breadcrumb-item>
        </b-breadcrumb>
      </div>
    </div>

    <b-card
      no-body
      class="card-custom card-border">
      <b-card-body>
        <b-row class="mb-8">
          <b-col
            lg="6"
            md="6"
            sm="6"
            cols="12">
            <div class="font-weight-bold mb-2">Server Details</div>
            <b-list-group
              v-if="selectedServerId !== null"
              class="mb-6 mb-sm-0">
              <b-list-group-item class="d-flex align-items-center">
                Name
                <span class="ml-auto">{{ selectedServer.name }}</span>
              </b-list-group-item>
              <b-list-group-item class="d-flex align-items-center">
                Location
                <span class="ml-auto">
                  <div class="d-flex align-items-center">
                    <img
                      :src="countryFlag(selectedServer.country)"
                      :alt="selectedServer.country"
                      class="w-20px mr-2 no-select rounded-sm bg-light border border-light rounded-sm" />
                    <span
                      class="d-inline-block mr-1 text-truncate"
                      style="max-width: 180px;">{{ countryName(selectedServer.country) }}, {{ selectedServer.location }}</span>
                  </div>
                </span>
              </b-list-group-item>
              <b-list-group-item class="d-flex align-items-center">
                IP
                <span class="ml-auto">{{ selectedServer.ip }}</span>
              </b-list-group-item>
              <b-list-group-item class="d-flex align-items-center">
                Status
                <span class="ml-auto font-size-xs">
                  <b-badge :variant="serverStatusBadge(selectedServer).variant">
                    <i :class="`fa fa-sm fa-${serverStatusBadge(selectedServer).icon} text-white`"></i>
                    {{ serverStatusBadge(selectedServer).text }}
                  </b-badge>
                </span>
              </b-list-group-item>
            </b-list-group>
          </b-col>
          <b-col
            lg="6"
            md="6"
            sm="6"
            cols="12">
            <div class="font-weight-bold mb-2">Recipe Details</div>
            <b-list-group v-if="selectedRecipeVersionId !== null">
              <b-list-group-item class="d-flex align-items-center">
                Name
                <span class="ml-auto">{{ selectedRecipe.name }}</span>
              </b-list-group-item>
              <b-list-group-item class="d-flex align-items-center">
                Slug
                <span class="ml-auto">{{ selectedRecipe.slug }}</span>
              </b-list-group-item>
              <b-list-group-item class="d-flex align-items-center">
                Script Version
                <span class="ml-auto">
                  <span class="font-size-xs">v</span>
                  <span>{{ selectedRecipeVersion.version }}</span>
                </span>
              </b-list-group-item>
              <b-list-group-item class="d-flex align-items-center">
                Script Size
                <span class="ml-auto">{{ selectedRecipeVersion.size_unit }}
                </span>
              </b-list-group-item>
            </b-list-group>
          </b-col>
        </b-row>
        <b-overlay
          :show="recipesResponses('installRecipeVersion')"
          spinner-type="border"
          variant="light"
          spinner-variant="primary"
          opacity="1"
          rounded>
          <b-row class="mb-4">
            <b-col cols="12">
              <div class="font-weight-bold mb-2">Server Response</div>
              <terminal :history='installRecipeResponse' />
            </b-col>
          </b-row>
          <b-button
            variant="outline-primary"
            size="sm"
            @click="reapeatInstallation()">
            <i class="fa fa-redo fa-sm" />
            <span>Repeat Installation</span>
          </b-button>
        </b-overlay>
      </b-card-body>
    </b-card>
  </div>
</template>

<script>
import Terminal from '@/view/content/Terminal.vue'
import {
  // mutations
  UNSELECT_SERVER,
} from '@/core/services/store/servers.module'
import {
  // actions
  INSTALL_RECIPE_VERSION,

  // mutations
  UNSELECT_RECIPE,
  CLEAR_RECIPE_VERSION,
  CLEAR_INSTALL_RECIPE_RESPONSE,
} from '@/core/services/store/recipes.module'
import { mapGetters } from 'vuex'
import { eventBus, toastOptions } from '@/main'

export default {
  name: 'ServersRecipeInstall',
  components: {
    Terminal,
  },
  computed: {
    ...mapGetters([
      'countryFlag',
      'countryName',

      'selectedRecipe',
      'selectedRecipeVersion',
      'selectedRecipeVersionId',
      'recipesResponses',
      'installRecipeResponse',

      'selectedServer',
      'selectedServerId',
      'serverStatusBadge',
    ]),

    pageDataIsEmpty() {
      return this.selectedRecipeVersionId == null || this.selectedServerId == null
    },
  },
  methods: {
    managePageBehavior() {
      this.pageDataIsEmpty ? this.backToServers() : this.installRecipeVersion()
    },

    backToServers() {
      this.$store.commit(UNSELECT_SERVER)
      this.$store.commit(UNSELECT_RECIPE)
      this.$store.commit(CLEAR_RECIPE_VERSION)
      this.$store.commit(CLEAR_INSTALL_RECIPE_RESPONSE)

      this.$router.push({ name: 'ServersList' }).catch(() => {})
    },

    installRecipeVersion() {
      this.$store.dispatch(INSTALL_RECIPE_VERSION, {
        version: this.selectedRecipeVersionId,
        server: this.selectedServerId,
      })
    },

    reapeatInstallation() {
      this.$store.commit(CLEAR_INSTALL_RECIPE_RESPONSE)
      this.installRecipeVersion()
    },

    showToast(message, variant) {
      this.$bvToast.toast(message, {
        variant: variant,
        ...toastOptions
      })
    }
  },
  created() {
    this.managePageBehavior()

    eventBus.$on('showToast/RecipeInstallation', ({ message, variant }) => {
      this.showToast(message, variant)
    })
  },
  destroyed() {
    this.$store.commit(CLEAR_INSTALL_RECIPE_RESPONSE)
    eventBus.$off('showToast/RecipeInstallation')
  },
}
</script>
